import { useTheme } from '@emotion/react';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { AxiosResponse } from 'axios';
import { FC, KeyboardEvent, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useNavigate } from 'react-router-dom';
import Checkbox from '../Checkbox/Checkbox';
import Link from '../Link/Link';
import { Loading } from '../Loading/Loading';
import { Row, RowLabel, StyledTreeViewContainer } from './MenuTreeView.styles';
import { RowProps, TreeViewProps } from './MenuTreeView.types';
import { Organization } from '../../services/menu/menu.types';
import { Skeleton, Stack } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import rem from '../../utils/rem/rem';

const LoadingRow = ({ level }: { level: number }) => (
  <Row level={level} data-testid='LoadingRow'>
    <Loading size={12} />
  </Row>
);

const TreeView: React.FC<TreeViewProps> = ({
  rows,
  level = 0,
  checkedRows,
  expandedRows,
  selectedRows,
  onRowClick,
  onCheckboxClick,
  defaultHandleCheckboxClick,
  dataStructure,
  childRowsChecked = false,
  parentId,
  parentRows,
  expandOnCheck = false,
  noRowsMessage = 'No records found.',
  handleRowsProps,
  handleHasUpdate,
  hasUpdate,
  externalTableUpdate,
  radioFilter,
  filterId
}) => {
  const isFirst = useRef(true);


  useEffect(() => {
    isFirst.current = false;
  }, []);

  useEffect(() => {
    if (rows && rows.length > 0 && handleRowsProps) {
      handleRowsProps(rows);
    }
  }, [hasUpdate]);

  return (
    <>
      {rows?.map((row: Organization) => {
        return (
          <TreeViewRow
            key={row.id}
            dataStructure={dataStructure}
            level={level}
            checkedRows={checkedRows}
            expandedRows={expandedRows}
            selectedRows={selectedRows}
            onRowClick={onRowClick}
            onCheckboxClick={onCheckboxClick}
            defaultHandleCheckboxClick={defaultHandleCheckboxClick}
            row={row}
            rowCheckedFromParent={childRowsChecked}
            parentId={parentId}
            expandOnCheck={expandOnCheck}
            handleAlertParent={handleHasUpdate}
            externalTableUpdate={externalTableUpdate}
            radioFilter={radioFilter}
            filterId={filterId}
          />
        );
      })}
      {(!rows || rows.length === 0) && (
        <Row level={level + 1}>
          <RowLabel>{noRowsMessage}</RowLabel>
        </Row>
      )}
    </>
  );
};

const TreeViewRow: FC<RowProps> = ({
  onRowClick,
  onCheckboxClick,
  defaultHandleCheckboxClick,
  level,
  checkedRows,
  expandedRows,
  selectedRows,
  dataStructure,
  row,
  rowCheckedFromParent = false,
  parentId,
  expandOnCheck,
  handleAlertParent,
  externalTableUpdate,
  radioFilter,
  filterId
}) => {
  const { fetchChildren, buildLabel, to, buildRequestBody, showCheckboxes, isLink, target } = dataStructure[row.type];
  const label = buildLabel({ parentId, row, dataStructure, level });
  const id = row.id;
  const navigate = useNavigate();
  const theme = useTheme();
  const ref = useRef<null | HTMLDivElement>(null);

  const isInExpandedRows = () => {
    if (expandedRows?.length === 0) return false;
    return (
      expandedRows?.findIndex(
        (expandedRow: Organization) =>
          expandedRow.id === id && expandedRow.type === row.type && isEqual(expandedRow.parents, row.parents)
      ) !== -1
    );
  };

  const isInCheckedRows = () => {
    if (checkedRows?.length === 0) return false;
    return (
      checkedRows?.findIndex(
        (checkedRow: Organization) =>
          checkedRow.id === id && checkedRow.type === row.type && isEqual(checkedRow.parents, row.parents)
      ) !== -1
    );
  };

  const isHighLighted = () => {
    if (radioFilter === undefined || radioFilter === '' || filterId === undefined || filterId === '') {
      return false
    }
    return radioFilter === row.type && filterId === row.id;
  }

  const [children, setChildren] = useState<any[]>(row.children || []);
  const [hasUpdate, setHasUpdate] = useState<boolean>(false);
  const rowIsInCheckedRows = useMemo(isInCheckedRows, [checkedRows?.length]);
  const rowIsInExpandedRows = useMemo(isInExpandedRows, [expandedRows?.length]);


  const defaultChecked = rowCheckedFromParent || rowIsInCheckedRows || false;

  const defaultExpanded = expandOnCheck ? defaultChecked || rowIsInExpandedRows || false : rowIsInExpandedRows || false;

  const [rowExpanded, setRowExpanded] = useState(defaultExpanded);
  const [rowChecked, setRowChecked] = useState(defaultChecked);

  // If row is the last (to) or we have 0 selected rows then indeterminate is always false
  const isIndeterminate = () => {
    if (selectedRows?.length === 0 || to) return false;
    return selectedRows?.some(selectedRow => selectedRow.parents[row.type] === row.id) || false;
  }

  const rowIsIndeterminate = useMemo(isIndeterminate, [selectedRows?.length, externalTableUpdate]);

  const handleRowsProps = (rows: any[]) => {
    if (row.children?.length === 0) setChildren(rows);
  }
  const handleHasUpdate = () => {
    setHasUpdate(!hasUpdate);
    if (handleAlertParent) handleAlertParent();
  }

  useEffect(() => {
    setRowExpanded(rowIsInExpandedRows);
  }, [rowIsInExpandedRows]);

  useEffect(() => {
    row.rowChecked = rowChecked;
    if (handleAlertParent) handleAlertParent();
  }, [rowChecked]);

  useEffect(() => {
    row.rowChecked = rowChecked;
  }, []);

  useEffect(() => {
    if (children.length > 0 && !rowChecked) {
      let childrenLength = children.length;
      let checkedAcc = 0;
      children.forEach(child => {
        if (child.rowChecked) checkedAcc++;
      });
      if (childrenLength === checkedAcc) {
        if (defaultHandleCheckboxClick) defaultHandleCheckboxClick(row, true);
        setRowChecked(true);
      }
    }
  }, [hasUpdate]);

  useEffect(() => {
    if (rowCheckedFromParent) {
      setRowChecked(true);
      // automatically expands the row when checked from parent if expandOnCheck is true
      if (expandOnCheck) {
        setRowExpanded(true);
        if (onRowClick) onRowClick(row, true);
      }
      if (defaultHandleCheckboxClick) defaultHandleCheckboxClick(row, true);
    } else if (!rowIsInCheckedRows) {
      setRowChecked(false);
    }
  }, [rowCheckedFromParent]);

  useEffect(() => {
    if (rowIsInCheckedRows) {
      // automatically expands the row when added to checkedRows if expandOnCheck is true
      if (expandOnCheck) {
        setRowExpanded(true);
        if (onRowClick) onRowClick(row, true);
      }
      setRowChecked(true);
    } else {
      setRowChecked(false);
    }
  }, [rowIsInCheckedRows]);

  const doScroll = () => {
    if (ref.current === null) {
      return;
    }
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  useEffect(() => {
    if (isHighLighted()) {
      doScroll()
    }
  }, [filterId]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.target as HTMLElement).tagName === 'INPUT') return;
    switch (event.key) {
      case 'Enter':
      case ' ':
        handleRowClick();
        break;
      default:
        return;
    }
  };

  const handleRowClick = (event?: MouseEvent) => {
    if ((event?.target as HTMLElement)?.tagName === 'INPUT') return;
    if (onRowClick) onRowClick(row, !rowExpanded);
    setRowExpanded(!rowExpanded);
  };

  // Cleans up children rows to initial state if row is unchecked and it is not expanded
  const clearChildren = () => {
    const nextChildren = children.map(value => {
      value.rowChecked = false;
      return value;
    });
    setChildren(nextChildren);
  }

  const handleCheckboxClick = () => {
    if (onCheckboxClick) onCheckboxClick(row, !rowChecked);
    // automatically expands the row when checked from parent if expandOnCheck is true
    if (expandOnCheck && !rowChecked) {
      setRowExpanded(true);
      if (onRowClick) onRowClick(row, true);
    }
    if (!rowChecked && !isInExpandedRows()) {
      clearChildren();
    }
    setRowChecked(!rowChecked);
  };

  if ((fetchChildren && buildRequestBody) || (children.length >= 0 && !to)) {
    let isLoading: boolean | undefined = false;
    let isFetched: boolean | undefined = true;
    let childRows = children;
    if (fetchChildren && buildRequestBody) {
      let fetch = fetchChildren(
        buildRequestBody({ parentId, row, dataStructure, level }),
        rowExpanded
      );
      childRows = children.length >= (fetch.data as AxiosResponse)?.data?.children?.length ? children : (fetch.data as AxiosResponse)?.data?.children;
      isLoading = fetch.isLoading;
      isFetched = fetch.isFetched;
    }
    const strLabel = `${label}`
  
    return (
      <>
        <Row
          tabIndex={0}
          level={level + 1}
          onClick={handleRowClick}
          onKeyDown={handleKeyDown}
          highLighted={isHighLighted()}
          data-testid={'TreeViewRow-' + id}
          id={id}
          ref={ref}
        >
          {rowExpanded ? (
            <ExpandMoreOutlinedIcon htmlColor={theme.colors.gray600} sx={{ margin: '0 8px' }} />
          ) : (
            <ChevronRightOutlinedIcon htmlColor={theme.colors.gray600} sx={{ margin: '0 8px' }} />
          )}
          {showCheckboxes && (
            <Checkbox
              checked={rowChecked}
              label=''
              id={id}
              onClick={handleCheckboxClick}
              readOnly
              aria-label='check row'
              indeterminate={(rowIsIndeterminate) && !rowChecked}
              testId={'TreeViewCheckbox-' + id}
            />
          )}
          <RowLabel level={level} expanded={rowExpanded}>
            {typeof label === 'string' ? strLabel : label}
          </RowLabel>
        </Row>
        {isFetched && rowExpanded ? (
          <TreeView
            dataStructure={dataStructure}
            rows={childRows}
            childRowsChecked={rowChecked}
            level={level + 1}
            checkedRows={checkedRows}
            expandedRows={expandedRows}
            selectedRows={selectedRows}
            onRowClick={onRowClick}
            onCheckboxClick={onCheckboxClick}
            defaultHandleCheckboxClick={defaultHandleCheckboxClick}
            parentId={id}
            expandOnCheck={expandOnCheck}
            handleRowsProps={handleRowsProps}
            handleHasUpdate={handleHasUpdate}
            hasUpdate={hasUpdate}
            radioFilter={radioFilter}
            filterId={filterId}
          />
        ) : isLoading && rowExpanded ? (
          <LoadingRow level={level === 0 ? level + 1 : 1} />
        ) : null}
      </>
    );
  }
  if (to) {
    let stringTo: string;
    let handleLinkRowClick: (event: MouseEvent) => void;

    const toReturnValue = to({ parentId, row, dataStructure, level });
    // to can either return a string or an object (which allows passing navigateOptions)
    if (typeof toReturnValue === 'string') {
      stringTo = toReturnValue;
      handleLinkRowClick = (event: MouseEvent) => {
        if ((event?.target as HTMLElement).tagName === 'INPUT') return;
        navigate(stringTo);
      };
    } else {
      const { navigateTo, navigateOptions } = toReturnValue;
      stringTo = navigateTo;
      handleLinkRowClick = (event: MouseEvent) => {
        if ((event?.target as HTMLElement).tagName === 'INPUT') return;
        navigate(stringTo, navigateOptions);
      };
    }

    if (children.length === 0) {
      return (
          <Row level={level + 1} data-link={true} onClick={handleLinkRowClick} highLighted={isHighLighted()} data-testid={'TreeViewRow-' + id} id={id} ref={ref}>
            {showCheckboxes && (
              <Checkbox checked={rowChecked} label='' id={id} onClick={handleCheckboxClick} testId={'TreeViewCheckbox-' + id} readOnly />
            )}
            <RowLabel level={level}>
              { isLink === false ? (<>{label}</>) : (<Link to={stringTo} label={label} target={target} />)
            }</RowLabel>
          </Row>
      );
    }

    return (
      <>
        <Row
          tabIndex={0}
          level={level + 1}
          onClick={handleRowClick}
          onKeyDown={handleKeyDown}
          highLighted={isHighLighted()}
          data-testid={'TreeViewRow-' + id}
          id={id}
          ref={ref}
        >
          {rowExpanded ? (
            <ExpandMoreOutlinedIcon htmlColor={theme.colors.gray600} sx={{ margin: '0 8px' }} />
          ) : (
            <ChevronRightOutlinedIcon htmlColor={theme.colors.gray600} sx={{ margin: '0 8px' }} />
          )}
          {showCheckboxes && (
            <Checkbox
              checked={rowChecked}
              label=''
              id={id}
              onClick={handleCheckboxClick}
              readOnly
              aria-label='check row'
              indeterminate={(rowIsIndeterminate) && !rowChecked}
              testId={'TreeViewCheckbox-' + id}
            />
          )}
          <RowLabel level={level}>{<Link to={stringTo} label={label} target={target} />}</RowLabel>
        </Row>
        {rowExpanded ? (
          <TreeView
            dataStructure={dataStructure}
            rows={children}
            childRowsChecked={rowChecked}
            level={level + 1}
            checkedRows={checkedRows}
            expandedRows={expandedRows}
            selectedRows={selectedRows}
            onRowClick={onRowClick}
            onCheckboxClick={onCheckboxClick}
            defaultHandleCheckboxClick={defaultHandleCheckboxClick}
            parentId={id}
            expandOnCheck={expandOnCheck}
            handleRowsProps={handleRowsProps}
            handleHasUpdate={handleHasUpdate}
            hasUpdate={hasUpdate}
            radioFilter={radioFilter}
            filterId={filterId}
          />
        ) : null}
      </>
    );
  }
  return null;
};

const muiTheme = createTheme();

const TreeViewContainer: FC<TreeViewProps> = (props) => {
  return (
    <>
      {props.isLoading ?
        <StyledTreeViewContainer height={props.height} borderRadius={props.borderRadius}>
          <ThemeProvider theme={muiTheme} key={"tree-view-mui-theme"}>
            <Stack spacing={rem(50)} key={"tree-view-mui-stack"}>
              {[...Array(20)].map((_: any, index: number) => (
                <Skeleton sx={{ bgcolor: '#f7f7f7' }} variant='rectangular' animation='wave' height={rem(50)}
                          key={"tree-view-mui-skeleton-" + index} />
              ))}
            </Stack>
          </ThemeProvider>
        </StyledTreeViewContainer>
        :
        (<StyledTreeViewContainer height={props.height} borderRadius={props.borderRadius}>
          <TreeView {...props} />
        </StyledTreeViewContainer>)
      }
    </>
  );
};

export default TreeViewContainer;